/**
 * DeltaChip — shared trend-delta indicator for KPI cards.
 *
 * V3-8-7: Extracted from Dashboard.tsx (V3-8-4) for reuse across
 * workspace and org dashboards.
 *
 * Sentiment encoding:
 *   - positive: increase is good (green ▲), decrease is bad (red ▼)
 *   - negative: increase is bad (red, NO triangle), decrease is good (green)
 *   - neutral: muted color, no directional encoding
 *
 * When value is null or 0, renders a muted dash (—).
 */

export function DeltaChip({ value, sentiment, label }: {
  value: number | null;
  sentiment: 'positive' | 'negative' | 'neutral';
  label?: string;
}) {
  if (value === null || value === undefined) {
    return (
      <span
        title={label ?? 'No prior data'}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          fontSize: 'var(--font-size-3xs)',
          fontFamily: 'var(--font-mono)',
          color: 'var(--text-dim)',
          fontWeight: 500,
        }}
      >
        —
      </span>
    );
  }

  if (value === 0) {
    return (
      <span
        title={label ?? 'No change'}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          fontSize: 'var(--font-size-3xs)',
          fontFamily: 'var(--font-mono)',
          color: 'var(--text-dim)',
          fontWeight: 500,
        }}
      >
        —
      </span>
    );
  }

  const isIncrease = value > 0;
  let chipColor: string;
  if (sentiment === 'neutral') {
    chipColor = 'var(--text-muted)';
  } else if (sentiment === 'positive') {
    chipColor = isIncrease ? 'var(--success-fg)' : 'var(--danger-fg)';
  } else {
    // negative sentiment: increase is bad
    chipColor = isIncrease ? 'var(--danger-fg)' : 'var(--success-fg)';
  }

  // Negative-sentiment metrics MUST NOT use up-arrow when increasing —
  // plain signed number only.
  const showTriangle = sentiment !== 'negative';
  const triangle = isIncrease ? '▲' : '▼';
  const sign = isIncrease ? '+' : '';

  return (
    <span
      title={label ?? `${sign}${value}`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '2px',
        fontSize: 'var(--font-size-3xs)',
        fontFamily: 'var(--font-mono)',
        fontWeight: 600,
        color: chipColor,
        lineHeight: 1,
      }}
    >
      {showTriangle ? (
        <span style={{ fontSize: '0.6em', lineHeight: 1 }}>{triangle}</span>
      ) : null}
      {sign}{value}
    </span>
  );
}
